import axios, { axiosInstance } from "@/plugins/axios";
import AppSettings from "@/models/api/AppSettings";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { Owner } from "@/models/api/Owner";
import CancellableHelper from "@/requests/CancellableHelper";
import { Expose, Type } from "class-transformer";
import { AutocompleteDataItem, AutocompleteDataResult } from "@/models/api/AutocompleteDataResult";
import { FileTmp } from "@/models/api/Files";
import { FiscalPrinterRegister } from "@/models/api/FiscalPrinterRegister";
import { Printer } from "@/models/api/Printer";
import { TaskSchedulerType } from "@/models/api/TaskSchedulerType";
import { ServiceType } from "@/models/api/serviceTypes/ServiceType";
import { Cashbox } from "@/models/api/Cashbox";
import { OrderGetTemporaryOrderSectionCurrencyItem } from "@/models/api/orders/temporary/OrderGetTemporaryOrderSection";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import FilterListItem from "@/models/api/filters/FilterListItem";
import { SelectItem } from "@/models/api/SelectItem";
import { AccountingChangeReason } from "@/models/api/changeReason/ChangeReason";

//#region Routes
export const URL_GENERAL_ERROR_500 = "/error-500";
export const URL_GET_APP_SETTINGS = "/application/settings";
export const URL_GET_APP_SETTING = "/application/settings/:key";
export const URL_GET_FILTER = "/filter";
export const URL_GET_SAVED_FILTERS = "/filter/GetSavedFilters";
export const URL_POST_ADD_FILTER = "/filter/CreateFilterSave";
export const URL_POST_DELETE_FILTER = "/filter/DeleteFilterSave";
export const URL_GET_OFFICE_PROFILE_CASHIER_AND_TRADERS = "/User/CashierAndTraders";
export const URL_GET_CASHIERS = "/User/Cashiers";
export const URL_GET_UNAVAILABLE_CASHIER = "/User/UnavailableCashier/${CashierId}";
export const URL_GET_CASHIERS_WITH_CODE = "/User/CashiersWithCode";
export const URL_GET_FISCAL_REGISTERS = "/ServiceType/FiscalPrinterRegister";
export const URL_GET_FISCAL_PRINTERS = "/Office/OfficeProfileFiscalPrinters";
export const URL_GET_OPEN_TEMPORARY_FILE = "/File/OpenTemporaryFile/${TemporaryFileId}";
export const URL_GET_FILE = "/File/${FileId}/OpenFile";
export const URL_POST_REMOVE_TEMPORARY_FILE = "/File/RemoveTemporaryFile/${TemporaryFileId}";
export const URL_POST_UPLOAD_FILE_TO_PREVIEW = "/File/${SessionId}/UploadFileToPreview";
export const URL_GET_TASK_TYPE_LIST = "/TaskScheduler/ScheduleEnumTypeList";
export const URL_GET_SERVICE_TYPE = "/ServiceType/ServiceType";
export const URL_GET_USER_CASHBOXES = "/User/Cashboxes";
export const URL_GET_CURRENCY_FOR_ORDER = "/Currency/GetCurrenciesForOrder";
export const URL_GET_COUNTRY_CODES_NAMES = "/Country/GetCountryCodesForVies";
export const URL_GET_FILTER_HEADERS = "/filter/TableHeaders";
export const URL_POST_SAVE_FILTER_HEADERS = "/filter/TableHeaders/Save";
export const URL_POST_CLEAR_FILTER_CACHE = "/filter/ClearFilterCache";
export const URL_GET_CHANGE_REASONS = "/ChangeReason";
export const URL_POST_GET_HANGFIRE_COOKIE = "/User/HangfireCookie";
//#endregion

//autocompletes
export const URL_GET_AUTOCOMPLETE_CONTRACTORS = "/Contractor/Autocomplete";
export const URL_GET_AUTOCOMPLETE_COUNTRIES = "/Country/CountryList";
export const URL_GET_AUTOCOMPLETE_AIRLINE_NAMES = "/Airline/AirlineList";
export const URL_GET_CONTRACTOR_BRANCH_NAMES = "/ContractorAdministration/GetBranchParents";

export const getAppSettings = async (): Promise<AppSettings> => {
  const response = {
    data: {
      showMailNotificationFunction: true,
      dataItemsPerPageOption: [5, 10, 25, 50],
    } as AppSettings,
  }; // await axios.get(URL_GET_APP_SETTINGS);
  return response.data as AppSettings;
};

interface GetFilterResponse {
  name: string;
  criterions: CriterionBase[];
}

export const getFilter = async (
  filterName: string,
  profileName: undefined | string = undefined
): Promise<GetFilterResponse> => {
  const response = await axios.get(URL_GET_FILTER, {
    params: { FilterName: filterName, FilterProfile: profileName },
  });
  return response.data as GetFilterResponse;
};

export const getSavedFilters = async (filterName: string): Promise<FilterListItem[]> => {
  const _getSavedFilters = new CancellableHelper<FilterListItem[]>(URL_GET_SAVED_FILTERS, FilterListItem);
  const response = await _getSavedFilters.get({ FilterName: filterName });
  return response;
};

const _addFilter = new CancellableHelper<unknown>(URL_POST_ADD_FILTER, null, true);
export const addFilter = async (
  filterName: string,
  profileName: string | null,
  criterions: Array<CriterionItemRequest>
): Promise<null> => {
  await _addFilter.post({ FilterName: filterName, FilterProfile: profileName }, JSON.stringify(criterions));
  return null;
};

const _deleteFilter = new CancellableHelper<null>(URL_POST_DELETE_FILTER, null, true);
export const deleteFilter = async (filterName: string, profileName: string | null): Promise<null> => {
  await _deleteFilter.post({ FilterName: filterName, FilterProfile: profileName }, null);
  return null;
};

const _postClearFilterCache = new CancellableHelper<null>(URL_POST_CLEAR_FILTER_CACHE, null, true);
export const postClearFilterCache = async (filterName: string): Promise<null> => {
  return await _postClearFilterCache.post({ filterName: filterName }, null);
};

export class GetOfficeCashierAndTradersResponse {
  @Type(() => Owner)
  @Expose()
  cashiers!: Array<Owner>;
  @Type(() => Owner)
  @Expose()
  traders!: Array<Owner>;
}

const _getOfficeProfileCashierAndTraders = new CancellableHelper<GetOfficeCashierAndTradersResponse>(
  URL_GET_OFFICE_PROFILE_CASHIER_AND_TRADERS,
  GetOfficeCashierAndTradersResponse
);
export const getOfficeProfileCashierAndTraders = async (): Promise<GetOfficeCashierAndTradersResponse> => {
  return await _getOfficeProfileCashierAndTraders.get();
};

const _getCashiers = new CancellableHelper<Array<Owner>>(URL_GET_CASHIERS, Owner);
export const getCashiers = async (): Promise<Array<Owner>> => {
  return await _getCashiers.get();
};
const _getCashiersWithCode = new CancellableHelper<Array<Owner>>(URL_GET_CASHIERS_WITH_CODE, Owner);
export const getCashiersWithCode = async (userId: string | null = null): Promise<Array<Owner>> => {
  return await _getCashiersWithCode.get({ UserId: userId });
};

const _getCashier = new CancellableHelper<Owner>(URL_GET_UNAVAILABLE_CASHIER, Owner);
export const getCashier = async (cashierId: number): Promise<Owner> => {
  return await _getCashier.get({ CashierId: cashierId });
};

const _getCountryCodesForVies = new CancellableHelper<Array<SelectItem>>(URL_GET_COUNTRY_CODES_NAMES, SelectItem);
export const getCountryCodesForVies = async (): Promise<Array<SelectItem>> => {
  return await _getCountryCodesForVies.get();
};

const _getFiscalRegisters = new CancellableHelper<Array<FiscalPrinterRegister>>(
  URL_GET_FISCAL_REGISTERS,
  FiscalPrinterRegister
);
export const getFiscalRegisters = async (): Promise<Array<FiscalPrinterRegister>> => {
  return await _getFiscalRegisters.get();
};

const _getFiscalPrinters = new CancellableHelper<Array<Printer>>(URL_GET_FISCAL_PRINTERS, Printer);
export const getFiscalPrinters = async (): Promise<Array<Printer>> => {
  return await _getFiscalPrinters.get();
};

const _getTaskType = new CancellableHelper<Array<TaskSchedulerType>>(URL_GET_TASK_TYPE_LIST, TaskSchedulerType);
export const getTaskTypeList = async (): Promise<Array<TaskSchedulerType>> => {
  return await _getTaskType.get();
};

const _getServiceTypes = new CancellableHelper<Array<ServiceType>>(URL_GET_SERVICE_TYPE, ServiceType);
export const getServiceTypes = async (): Promise<Array<ServiceType>> => {
  return await _getServiceTypes.get();
};

const _getChangeReasons = new CancellableHelper<AccountingChangeReason[]>(
  URL_GET_CHANGE_REASONS,
  AccountingChangeReason
);
export const getChangeReasons = async (): Promise<AccountingChangeReason[]> => {
  return await _getChangeReasons.get();
};

//TODO - hkopczacki refactor file opening
export const getOpenTemporaryFile = async (fileId: number, download = false): Promise<void> => {
  const response = await axiosInstance.get(
    URL_GET_OPEN_TEMPORARY_FILE.replace("${TemporaryFileId}", fileId.toString()),
    { responseType: "blob" }
  );
  const disposition = response.headers["content-disposition"];
  const contentType = response.headers["content-type"];

  let filename = "document";
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(disposition);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, "");
  }

  const blob = new Blob([response.data], {
    type: contentType,
  });

  const file = new File([blob], filename, {
    type: contentType,
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(file);
  link.target = "_blank";

  if (download) {
    link.download = filename;
  }

  link.click();
};

export const getOpenFile = async (fileId: string, download = false): Promise<void> => {
  const response = await axiosInstance.get(URL_GET_FILE.replace("${FileId}", fileId.toString()), {
    responseType: "blob",
  });
  const disposition = response.headers["content-disposition"];
  const contentType = response.headers["content-type"];

  let filename = "document";
  const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
  const matches = filenameRegex.exec(disposition);
  if (matches != null && matches[1]) {
    filename = matches[1].replace(/['"]/g, "");
  }

  const blob = new Blob([response.data], {
    type: contentType,
  });

  const file = new File([blob], filename, {
    type: contentType,
  });

  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(file);
  link.target = "_blank";

  if (download) {
    link.download = filename;
  }

  link.click();
};

export const getFile = async (fileId: string): Promise<Blob> => {
  const response = await axiosInstance.get(URL_GET_FILE.replace("${FileId}", fileId.toString()), {
    responseType: "blob",
  });
  return new Blob([response.data], { type: response.headers["content-type"] });
};

export const postRemoveTemporaryFile = async (fileId: number): Promise<void> => {
  const _postDeleteTemporaryFile = new CancellableHelper<null>(URL_POST_REMOVE_TEMPORARY_FILE, null, true);
  await _postDeleteTemporaryFile.post({ TemporaryFileId: fileId });
};

const _getCriterionAutocompleteData = new CancellableHelper<AutocompleteDataResult>("", AutocompleteDataItem, true);
export const getCriterionAutocompleteData = async (url: string, search: string): Promise<AutocompleteDataResult> => {
  _getCriterionAutocompleteData.url = url;
  return await _getCriterionAutocompleteData.get({ value: search });
};

export const postUploadFileToPreview = async (files: Array<File>, sessionId: string): Promise<Array<FileTmp>> => {
  const url = URL_POST_UPLOAD_FILE_TO_PREVIEW.replace("${SessionId}", sessionId);
  const formData = new FormData();
  for (const file of files) {
    formData.append(file.name, file);
  }
  const response = await axiosInstance.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data as Array<FileTmp>;
};

const _getStringAutocompleteData = new CancellableHelper<Array<string>>("");
export const getStringAutocompleteData = async (
  url: string,
  search: string,
  page: number | undefined = undefined,
  count: number | undefined = undefined
): Promise<Array<string>> => {
  _getStringAutocompleteData.url = url;
  return await _getStringAutocompleteData.get({
    searchValue: search,
    page: page,
    count: count,
  });
};

export const getObjectAutocompleteData = async <T>(
  url: string,
  search: string,
  page: number | undefined = undefined,
  count: number | undefined = undefined
): Promise<Array<T>> => {
  const _getObjectAutocompleteData = new CancellableHelper<Array<T>>("");
  _getObjectAutocompleteData.url = url;
  return await _getObjectAutocompleteData.get({
    searchValue: search,
    page: page,
    count: count,
  });
};

const _getUserCashboxes = new CancellableHelper<Array<Cashbox>>(URL_GET_USER_CASHBOXES, Cashbox);
export const getUserCashboxes = async (): Promise<Array<Cashbox>> => {
  return await _getUserCashboxes.get({});
};

const _getCurrenciesForOrder = new CancellableHelper<Array<OrderGetTemporaryOrderSectionCurrencyItem>>(
  URL_GET_CURRENCY_FOR_ORDER,
  OrderGetTemporaryOrderSectionCurrencyItem
);
export const getCurrenciesForOrder = async (): Promise<Array<OrderGetTemporaryOrderSectionCurrencyItem>> => {
  return await _getCurrenciesForOrder.get();
};

export const getFilterHeaders = async (filterName: string): Promise<string> => {
  const response = await axios.get(URL_GET_FILTER_HEADERS, {
    params: { FilterName: filterName },
  });
  return response.data as string;
};

export const postSaveFilterHeaders = async (filterName: string, keys: string): Promise<void> => {
  await axios.post(URL_POST_SAVE_FILTER_HEADERS, null, {
    params: { FilterName: filterName, Keys: keys },
  });
};

export const getHangfireCookie = async (): Promise<null> => {
  const _getHangfireCookie = new CancellableHelper<null>(URL_POST_GET_HANGFIRE_COOKIE, null, true);
  return await _getHangfireCookie.post();
};
